<template>
  <div class="visitor-pricing__data table-sequence">
    <v-data-table
      class="data__table with-background"
      ref="table"
      :headers="headers"
      :items="data"
      :hide-default-header="isEmptyTable"
      hide-default-footer
      disable-pagination
      :mobile-breakpoint="mobileBreakpoint"
    >
      <template v-slot:top>
        <v-toolbar class="v-data-table__status-bar" flat>
          <v-toolbar-title class="v-data-table__title">
            Apply sequence on
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <template v-if="showCopyPaste">
            <v-btn
              class="pkmn-button-small mr-2"
              outlined
              color="primary"
              @click="$emit('copy')"
            >
              Copy
            </v-btn>
            <v-btn
              class="pkmn-button-small"
              color="primary"
              :loading="pasteLoading"
              :disabled="!canPaste || pasteLoading"
              @click="$emit('paste')"
            >
              Paste
            </v-btn>
          </template>
          <v-dialog
            v-model="dialog"
            :fullscreen="isMobile"
            width="80%"
            max-width="512px"
            @click:outside="close"
            v-if="!hideActions"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="pkmn-button-small ml-2"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
              Add
              </v-btn>
            </template>
            <v-card class="form-dialog form-dialog--payout-id">
              <v-icon
                class="form-dialog__button form-dialog__button--close"
                @click="close"
              >
                mdi-close
              </v-icon>

              <h1 class="form-dialog__title">Sequence price time</h1>

              <p class="form-dialog__subtitle">
                Choose when the sequence price should apply
              </p>

              <v-form ref="sequenceForm" class="data-table-sequence-form">
                <v-row class="form-row input-row">
                  <v-col cols="6">
                    <div class="text-input-label">From</div>
                    <v-text-field
                      class="form-font font-weight-bold"
                      v-model="timeOfDayStart"
                      solo
                      flat
                      dense
                      background-color="#F6F6F6"
                      :rules="[rules.isValidTime]"
                    />
                  </v-col>

                  <v-col cols="6">
                    <div class="text-input-label">Until</div>
                    <v-text-field
                      class="form-font font-weight-bold"
                      v-model="timeOfDayEnd"
                      solo
                      flat
                      dense
                      background-color="#F6F6F6"
                      :rules="[rules.isValidTime]"
                    />
                  </v-col>
                </v-row>
                <v-row class="form-row input-row">
                  <v-col>
                    <div class="text-input-label">Set weekday price</div>

                    <v-chip-group
                      v-model="weekdaySelection"
                      outlined
                      multiple
                      class="week-price-chip-group"
                      active-class="v-chip--active"
                    >
                      <v-chip
                        class="v-chip"
                        v-for="(item, i) in weekday"
                        :key="i"
                      >
                        {{ item }}
                      </v-chip>
                    </v-chip-group>
                  </v-col>
                </v-row>
                  <v-row class="form-row">
                  <v-col>
                  <v-switch
                        color="primary"
                        v-model="applyOnHoliday"
                        class="switch-wrapper"
                  >
                        <template v-slot:label>
                        <span class="input__label">Apply on holidays</span>
                        </template>
                  </v-switch>
                  </v-col>
                  </v-row>
                <v-row class="form-dialog__button-wrap">
                  <v-col>
                    <v-btn
                      class="pkmn-button pkmn-button--has-border"
                      color="primary"
                      @click="applyPeriod"
                    >
                      Confirm
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-dialog>

          <v-dialog
            v-model="dialogDelete"
            :fullscreen="isMobile"
            width="80%"
            max-width="512px"
            @click:outside="closeDelete"
          >
            <v-card class="form-dialog form-dialog--payout-id">
              <v-icon
                class="form-dialog__button form-dialog__button--close"
                @click="closeDelete"
              >
                mdi-close
              </v-icon>

              <h1 class="form-dialog__title">Delete sequence price?</h1>

              <p class="form-dialog__subtitle">
                This action can't be undone. The next step will be moved higher
                in the order
              </p>
              <v-row class="form-dialog__button-wrap">
                  <v-col cols="6" class="text-left">
                    <v-btn
                      class="pkmn-button"
                      outlined
                      color="danger"
                      @click="closeDelete"
                    >
                      Back
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn
                      class="pkmn-button pkmn-button--delete pkmn-button--has-border"
                      color="danger"
                      @click="deleteItemConfirm"
                    >
                      Delete
                    </v-btn>
                  </v-col>
                </v-row>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:item.id="{ item }">
        <div class="mobile-row__action" v-if="!hideActions">
          <div>
            <v-btn
              class="pkmn-button pkmn-button--no-padding data__action-button"
              text
              color="primary"
              @click="editSchedule(item)"
            >
              <img src="@/assets/button-edit.svg" />
            </v-btn>
            <v-btn
              class="pkmn-button pkmn-button--no-padding data__action-button"
              text
              color="warning"
              @click="deleteSchedule(item)"
            >
              <img src="@/assets/button-delete.svg" />
            </v-btn>
          </div>
        </div>
      </template>
      <template v-slot:item.time_of_day_start="{ item }">
        {{ toHHmmss(item.time_of_day_start) }}
      </template>
      <template v-slot:item.time_of_day_end="{ item }">
        {{ toHHmmss(item.time_of_day_end) }}
      </template>
      <template v-slot:item.days_of_week="{ item }">
        {{ weekdaysString(item) }}
      </template>
      <template v-if="isEmptyTable" v-slot:body="">
        <div></div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import rules from '@/helpers/validation'
import { mapGetters } from 'vuex'
import { TimeEnum } from '@/enums'

export default {
  name: 'DataTableApplySequence',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    currency: {
      type: String,
      default: '$'
    },
    hideActions: {
      type: Boolean
    },
    showCopyPaste: { type: Boolean, default: false },
    canPaste: { type: Boolean, default: false },
    pasteLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      headers: [
        { text: 'From', value: 'time_of_day_start', sortable: false },
        { text: 'Until', value: 'time_of_day_end', sortable: false },
        { text: 'Weekdays', value: 'days_of_week', sortable: false },
        { text: '', value: 'id', sortable: false }
      ],
      period_ids:[],
      timeOfDayStart: null,
      timeOfDayEnd: null,
      weekday: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
      weekdaySelection: [],
      applyOnHoliday: false,
      dialog: false,
      dialogDelete: false,
      mobileBreakpoint: 767, // screen width in pixel to switch to mobile view
      rules: rules
    }
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
    dialogDelete(val) {
      val || this.closeDelete()
    }
  },

  computed: {
    ...mapGetters(['timeDayUnitOptions', 'currentProductCurrencySymbol']),
    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint
    },
    isNewItem() {
      return this.period_ids.length === 0
    },
    isEmptyTable() {
      return Object.keys(this.data).length === 0
    }
  },

  methods: {
    toHHmmss(timeStr) {
      if (!timeStr) return timeStr
      const parts = String(timeStr).split(':')
      const h = (parts[0] || '00').padStart(2, '0')
      const m = (parts[1] || '00').padStart(2, '0')
      const s = (parts[2] || '00').padStart(2, '0')
      return `${h}:${m}:${s}`
    },
    weekdaysString(item) {
      const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      let weekdaysString = ''
      if (item.days_of_week && Array.isArray(item.days_of_week)) {
        weekdaysString = item.days_of_week.map((dayNum) => weekdays[dayNum - 1]).join(', ')
      }
      if (item.applies_on_holiday) {
        weekdaysString += ', Holidays'
      }
      return weekdaysString
    },
    priceString(item) {
      return item.ui_price_data
        ? `${item.ui_price_data?.price} ${
            this.currentProductCurrencySymbol
          } / ${item.ui_price_data?.price_time_unit?.value} ${
            TimeEnum[item.ui_price_data?.price_time_unit?.unit]
          }`
        : `${item.price} ${this.currentProductCurrencySymbol} / ${
            item.time_period?.value
          } ${TimeEnum[item.time_period?.unit]}`
    },
    editSchedule(item) {
      this.weekdaySelection = item.days_of_week.map((day) => day - 1)
      this.timeOfDayStart = this.toHHmmss(item.time_of_day_start)
      this.timeOfDayEnd = this.toHHmmss(item.time_of_day_end)
      this.applyOnHoliday = item.applies_on_holiday
      this.period_ids = item.period_ids
      this.dialog = true
    },
    clearForm() {
      this.weekdaySelection = []
      this.timeOfDayStart = null
      this.timeOfDayEnd = null
      this.applyOnHoliday = false
    },
    deleteSchedule() {
      this.dialogDelete = true
    },
    applyPeriod() {
      if (this.$refs.sequenceForm.validate()) {
        if (this.isNewItem) {
          const weekdays = this.weekdaySelection.map((day) => day + 1)
          const newItem = {
            time_of_day_start: this.toHHmmss(this.timeOfDayStart),
            time_of_day_end: this.toHHmmss(this.timeOfDayEnd),
            days_of_week: weekdays,
            applies_on_holiday: this.applyOnHoliday,
            parkings_check_level: 1
          }
          this.$emit('submit', newItem)
        } else {
          const weekdays = this.weekdaySelection.map((day) => day + 1)
          const newItem = {
            time_of_day_start: this.toHHmmss(this.timeOfDayStart),
            time_of_day_end: this.toHHmmss(this.timeOfDayEnd),
            days_of_week: weekdays,
            applies_on_holiday: this.applyOnHoliday,
            parkings_check_level: 1,
            schedule_id: this.data[0].schedule_id,
            sequence_id: this.data[0].sequence_id,
            period_ids: this.data[0].period_ids
          }
          this.$emit('update', newItem)
        }
        this.close()
      }
    },
    close() {
      this.dialog = false
    },
    closeDelete() {
      this.dialogDelete = false
    },
    deleteItemConfirm() {
      this.$emit('delete', this.data[0])
      this.closeDelete()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/common';

.v-data-table__title {
  @include text-body;
  font-weight: 700;
  color: $text-color-dark;
}

.form-row {
  min-height: 60px;
  max-height: 70px;
}

.pkmn-button--delete {
  color: $color-white;
}

.schedule-table {
  background-color: $color-alt-background !important;
}
.form-dialog__button-wrap {
  margin-top: 24px !important;
}
</style>
<style lang="scss">
.data-table-sequence-form {
  .font-weight-bold input,
  .font-weight-bold .v-select__selection {
    font-weight: 900;
    line-height: 20px;
  }
  .input-mr {
    margin-right: 12px;
  }
  .input-ml {
    margin-left: 12px;
  }
}
</style>
